import { z } from 'zod';
import type { RecurrencePattern } from '../lib/rrule';

/**
 * Schema for event form values.
 * Uses local datetime strings for form inputs (not ISO).
 */
export const eventFormSchema = z
  .object({
    calendarId: z.string().uuid(),
    title: z.string().min(1, 'Title is required').max(255),
    description: z.string().max(2000),
    address: z.string().max(500).optional().default(''),
    phone: z.string().max(50).optional().default(''),
    startAt: z.string(), // local datetime string (YYYY-MM-DDTHH:MM or YYYY-MM-DD)
    endAt: z.string(), // local datetime string
    allDay: z.boolean(),
    tags: z.array(z.string()),
    recurrence: z.custom<RecurrencePattern>(),
    asKocal: z.boolean().optional().default(false),
  })
  .refine(
    data => {
      const start = new Date(data.startAt);
      const end = new Date(data.endAt);
      const isValid = data.allDay
        ? end >= start
        : end > start;
      console.log(
        '[eventFormSchema validate] allDay:',
        data.allDay,
        '| startAt:',
        data.startAt,
        '| endAt:',
        data.endAt,
        '| start:',
        start.toISOString(),
        '| end:',
        end.toISOString(),
        '| valid:',
        isValid
      );
      return isValid;
    },
    {
      message: 'End time must be after start time',
      path: ['endAt'],
    }
  );

export type EventFormValues = z.input<
  typeof eventFormSchema
>;

/**
 * Default form values for creating a new event on a specific date.
 */
export function getDefaultFormValues(
  date: Date
): EventFormValues {
  const pad = (n: number) => String(n).padStart(2, '0');

  const start = new Date(date);
  start.setHours(9, 0, 0, 0);

  const end = new Date(date);
  end.setHours(10, 0, 0, 0);

  const toLocalDatetime = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

  return {
    calendarId: '', // Set by caller
    title: '',
    description: '',
    address: '',
    phone: '',
    startAt: toLocalDatetime(start),
    endAt: toLocalDatetime(end),
    allDay: false,
    tags: [],
    recurrence: { type: 'none' },
    asKocal: false,
  };
}

/**
 * Convert ISO datetime string to local datetime string for form inputs.
 * For all-day events (UTC midnight), use UTC getters to preserve the exact calendar date.
 */
export function isoToLocalDatetime(
  iso: string,
  allDay = false
): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  if (allDay) {
    // Use UTC getters to preserve the exact calendar date for all-day events
    return (
      `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` +
      `T00:00`
    );
  }
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/**
 * Convert local datetime string to ISO string for API.
 * For all-day events, treats the date as UTC midnight to preserve the calendar date.
 */
export function localDatetimeToIso(
  local: string,
  allDay = false
): string {
  if (allDay) {
    // Extract date components and create UTC midnight timestamp
    // local format: YYYY-MM-DDTHH:MM or YYYY-MM-DD
    const datePart = local.slice(0, 10);
    const [year, month, day] = datePart
      .split('-')
      .map(Number);
    const d = new Date(
      Date.UTC(year, month - 1, day, 0, 0, 0)
    );
    return d.toISOString();
  }
  return new Date(local).toISOString();
}

/**
 * Parse comma-separated tags string to array.
 */
export function parseTags(tagsRaw: string): string[] {
  return tagsRaw
    .split(',')
    .map(t => t.trim())
    .filter(Boolean);
}

/**
 * Format tags array to comma-separated string.
 */
export function formatTags(tags: string[]): string {
  return tags.join(', ');
}
